// ========== frontend/src/pages/Buy_checkout_no_influencer.js

import Header from "../Header";
import Footer from "../Footer";
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import React, { useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCardForm from '../components/StripeCardForm';
import '../css/checkout.css';

// Cover art per format — previously every branch pointed at the same
// paperback image regardless of what was actually purchased.
const BOOK_IMAGES = {
  EBOOK: '/images/new_images/11.png',
  AUDIOBOOK: '/images/new_images/10.png',
  PAPERBACK: '/images/new_images/3.png',
  HARDCOVER: '/images/new_images/3.png',
};

const FORMAT_LABELS = {
  EBOOK: 'eBook',
  AUDIOBOOK: 'Audiobook',
  PAPERBACK: 'Paperback',
  HARDCOVER: 'Hardcover',
};

// Kept consistent with this file's existing convention of hardcoding the
// absolute /public/public/ URL rather than routing through apiClient.js.
const API_BASE = 'https://juniorpersist.com/public/public/api';
// const API_BASE = 'http://127.0.0.1:8000/api';

// loadStripe() (and the publishable-key fetch feeding it) should only ever
// run once, not on every render/remount — cached at module scope per
// Stripe's own guidance.
let stripePromiseCache = null;
function getStripePromise() {
  if (!stripePromiseCache) {
    stripePromiseCache = fetch(API_BASE + '/stripe/public-key')
      .then((res) => res.json())
      .then((data) => loadStripe(data.key));
  }
  return stripePromiseCache;
}

function Buy_checkout() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // All hooks must run unconditionally on every render (Rules of Hooks),
  // so these are declared before the early-return guard below rather than
  // after it.
  const [processing, setProcessing] = useState(false);
  const [ErrorMessage, setErrorMessage] = useState("");

  // Stripe Elements (embedded card form) state — replaces the old
  // hidden-fields <form> that POSTed to /order/stripe and redirected to a
  // Stripe-hosted page. The customer now never leaves this page.
  const [cardFormOpen, setCardFormOpen] = useState(false);
  const [cardFormLoading, setCardFormLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise] = useState(() => getStripePromise());

  // Direct/refresh navigation with no order in hand — nothing to check
  // out, send them back to pick a format instead of crashing on state.type.
  if (!state) {
    return <Navigate to="/BuyBook" replace />;
  }

  const ID_ACHAT = Date.now();

  let TOTAL;
  let shipping; // Declaring shipping outside the if-else block

  if ((state.type === 'PAPERBACK' || state.type === 'HARDCOVER') && (state.country === 'USA' || state.country === 'Canada')) {
    if (state.qty == 1) {
      shipping = 7.99;
    }
    if (state.qty == 2) {
      shipping = 10.99;
    }
    if (state.qty == 3) {
      shipping = 13.99;
    }
    if (state.qty >= 4 && state.qty <= 5) {
      shipping = 16.99;
    }
    if (state.qty >= 6 && state.qty <= 10) {
      shipping = 22.99;
    }
    if (state.qty >= 11 && state.qty <= 20) {
      shipping = 34.99;
    }
    if (state.qty >= 21 && state.qty <= 35) {
      shipping = 49.99;
    }
    if (state.qty >= 36 && state.qty <= 50) {
      shipping = 69.99;
    }
    if (state.qty >= 51 && state.qty <= 75) {
      shipping = 99.99;
    }
    if (state.qty >= 76 && state.qty <= 99) {
      shipping = 139.99;
    }
    if (state.qty >= 100) {
      shipping = 0;
    }

    TOTAL = state.amount + shipping;
  } else if (state.type === 'PAPERBACK' || state.type === 'HARDCOVER') {
    shipping = 48;
    TOTAL = state.amount + shipping;
  } else {
    TOTAL = state.amount;
  }

  const isPhysical = state.type === 'PAPERBACK' || state.type === 'HARDCOVER';
  const bookImage = BOOK_IMAGES[state.type] || BOOK_IMAGES.PAPERBACK;
  const formatLabel = FORMAT_LABELS[state.type] || state.type;
  const shippingLabel = shipping === 0 ? 'Free' : ('$' + Number(shipping).toFixed(2));

  // ---------- PayPal (unchanged from before) ----------
  const createOrder = (data, actions) => {
    setErrorMessage("");
    return actions.order
      .create({
        purchase_units: [
          {
            description: "Sunflower",
            amount: {
              currency_code: "USD",
              value: Number(TOTAL).toFixed(2),
            },
          },
        ],
        application_context: {
          shipping_preference: "NO_SHIPPING",
        },
      })
      .then((orderID) => {
        return orderID;
      });
  };

  const onApprove = (data, actions) => {
    setProcessing(true);
    return actions.order.capture().then(function (details) {
      window.location.replace('https://juniorpersist.com/public/public/store_PreOrder_no_influencer?name=' + state.name
        + '&order_id=' + ID_ACHAT
        + '&phone=' + state.phone
        + '&email=' + state.email
        + '&type=' + state.type
        + '&qty=' + state.qty
        + '&amount=' + TOTAL
        + '&description=' + state.description
        + '&influencer_id=' + state.influencer_id
        + '&street=' + state.street
        + '&city=' + state.city
        + '&state_province=' + state.state_province
        + '&postal_ZIP_code=' + state.postal_ZIP_code
        + '&country=' + state.country
      );
    }).catch(() => {
      setProcessing(false);
      setErrorMessage("We couldn't confirm your PayPal payment. Please try again.");
    });
  };

  const onError = (data, actions) => {
    setProcessing(false);
    setErrorMessage("An error occurred with your payment. Please try again, or use the card option above.");
  };

  // ---------- Stripe Elements (embedded card form) ----------
  async function openCardForm() {
    if (cardFormOpen || cardFormLoading) return;
    setErrorMessage("");
    setCardFormLoading(true);

    try {
      const res = await fetch(API_BASE + '/stripe/payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: state.name,
          phone: state.phone,
          email: state.email,
          type: state.type,
          qty: state.qty,
          amount: TOTAL,
          description: state.description,
          street: state.street,
          city: state.city,
          state_province: state.state_province,
          postal_ZIP_code: state.postal_ZIP_code,
          country: state.country,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.client_secret) {
        throw new Error(data.message || 'Could not start card payment. Please try again.');
      }

      setClientSecret(data.client_secret);
      setCardFormOpen(true);
    } catch (err) {
      setErrorMessage(err.message || 'Could not start card payment. Please try again.');
    } finally {
      setCardFormLoading(false);
    }
  }

  async function handleStripeSuccess(paymentIntentId) {
    setProcessing(true);
    try {
      const res = await fetch(API_BASE + '/stripe/confirm-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ payment_intent_id: paymentIntentId }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || 'Could not confirm your order.');
      }

      const params = new URLSearchParams({ type: data.type || state.type });
      if (data.code) params.set('code', data.code);
      navigate('/PreOrder_success?' + params.toString());
    } catch (err) {
      // Payment already succeeded with Stripe at this point — this only
      // means the immediate confirmation call failed, not that the order
      // is lost. The webhook (server-to-server, independent of this
      // browser) still fulfills it as the durable fallback.
      setProcessing(false);
      setErrorMessage(
        "Your payment was successful, but we couldn't confirm your order automatically. " +
        "Please check your email for confirmation, or contact us if you don't receive it shortly."
      );
    }
  }

  function handleStripeFailure(message) {
    setErrorMessage(message);
  }

  return (
    <PayPalScriptProvider
      options={{
        //  LIVE
        "client-id": "AVRfkzj1lNDb1NLTdQBgf2qLz6jppRDP3AG8lDF31MKm22LTJie4kAT_KC_vQYIgfITBwMD228DqJt1a&disable-funding=paylater,venmo,card",

        //  SANBOX
        // "client-id": "AVWTBxdPJ6aLgCU2JRyxqLHjcUyRNKyuZIeuhuwa_pPGwwUtHT_Vbw76Kt90t0AAr6Mtn2FEbun2KZxV&disable-funding=paylater,venmo,card",
      }}
    >
      <div className="checkout-page">
        <Header />

        {processing && (
          <div className="checkout-processing">
            <div className="spinner" />
            <p>Confirming your payment…</p>
          </div>
        )}

        <div className="checkout-hero">
          <div className="eyebrow"><i className="bi bi-lock-fill" /> Secure Checkout</div>
          <h1>You're almost there</h1>
        </div>

        {ErrorMessage && (
          <div className="checkout-error-banner">
            <div className="inner">{ErrorMessage}</div>
          </div>
        )}

        <div className="checkout-grid">
          {/* ===== Order summary ===== */}
          <div className="checkout-card">
            <h2>Order summary</h2>

            <div className="checkout-book-preview">
              <img src={bookImage} alt={`Persist, Persist, Persist — ${formatLabel}`} />
              <div>
                <div className="checkout-format-badge">{formatLabel}</div>
                <div className="title">Persist, Persist, Persist.</div>
              </div>
            </div>

            <div className="checkout-summary-row">
              <span>Price</span>
              <span>${Number(state.amount / state.qty).toFixed(2)} &times; {state.qty}</span>
            </div>
            <div className="checkout-summary-row">
              <span>Subtotal</span>
              <span>${Number(state.amount).toFixed(2)}</span>
            </div>
            {isPhysical && (
              <div className="checkout-summary-row">
                <span>Shipping</span>
                <span>{shippingLabel}</span>
              </div>
            )}
            <div className="checkout-summary-row">
              <span>Taxes</span>
              <span>Included</span>
            </div>

            <div className="checkout-total-row">
              <span className="label">Total</span>
              <span className="value">${Number(TOTAL).toFixed(2)} USD</span>
            </div>

            {state.description && <div className="checkout-note">"{state.description}"</div>}
          </div>

          {/* ===== Payment ===== */}
          <div className="checkout-card">
            <h2>Your information</h2>

            <div className="checkout-customer">
              <div className="name">{state.name}</div>
              <span className="detail">{state.phone}</span>
              <span className="detail">{state.email}</span>
            </div>

            {/* ---- Card payment, embedded via Stripe Elements ----
                 Stays on this page the entire time; no more redirect to
                 checkout.stripe.com. */}
            {!cardFormOpen ? (
              <button className="checkout-stripe-btn" type="button" onClick={openCardForm} disabled={cardFormLoading}>
                <div className="checkout-stripe-label">
                  <i className="bi bi-credit-card-fill" /> {cardFormLoading ? 'Loading…' : 'Pay with Card'}
                </div>
              </button>
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
                <StripeCardForm onSuccess={handleStripeSuccess} onFailure={handleStripeFailure} />
              </Elements>
            ) : null}

            <div className="checkout-or-divider">or</div>

            <div className="checkout-paypal-wrap">
              <PayPalButtons style={{ layout: "vertical" }} createOrder={createOrder} onApprove={onApprove} onError={onError} />
            </div>

            <div className="checkout-payment-icons">
              <img src={"/images/icons/payment_new.png"} alt="Accepted payment methods" />
            </div>
          </div>
        </div>

        <div className="checkout-trust-row">
          <span><i className="bi bi-shield-lock-fill" /> 256-bit encrypted checkout</span>
          <span><i className="bi bi-arrow-counterclockwise" /> Questions? hello@juniorpersist.com</span>
        </div>

        <br />

        <Footer />
      </div>
    </PayPalScriptProvider>
  )
}


export default Buy_checkout
